import { action, computed, makeAutoObservable } from 'mobx'

import { IRootStore } from 'store/types'
import { USER_PERMISSION } from 'shared/types/types'

import { AppCountryPhoneViewModel } from '../../UserModule/stores/models/AppCountryPhoneViewModel'
import { ELOCK_PERMISSIONS, EmployeeActiveStatus } from '../data'
import { UserELockRoleRequest } from '../types/typesDTO'
import { ViewEmployeeApiStore } from './ViewEmployeeApiStore'

export class ViewEmployeeUiStore {
  activeStatus = EmployeeActiveStatus.active

  selectedStep = 1
  lastName = ''
  firstName = ''
  snils = ''
  code = ''
  token = ''

  selectedRole = USER_PERMISSION.PERM_EMPLOYEE
  selectedELockRole = ELOCK_PERMISSIONS.none
  selectedUserId = 0
  eLockRoleList: UserELockRoleRequest[] = []

  search = ''

  readonly phoneModel = new AppCountryPhoneViewModel()
  readonly apiStore = new ViewEmployeeApiStore(this)

  constructor(readonly rootStore: IRootStore) {
    makeAutoObservable(this)
  }

  get employeeStore() {
    return this.rootStore.employeeStore
  }

  get employeeRoleStore() {
    return this.rootStore.employeeRoleStore
  }

  get employeeELockRoleStore() {
    return this.rootStore.employeeELockRoleStore
  }

  get viewPvzUiStore() {
    return this.rootStore.viewPvzUiStore
  }

  get selectedPvz() {
    return this.viewPvzUiStore.selectedPvz
  }

  get employeeList() {
    return this.employeeStore.list
  }

  get loadingEmployeeList() {
    return this.employeeStore.loading
  }

  @computed
  get employeeRoleList() {
    let employeeRoleList = this.employeeRoleStore.list

    if (this.search.trim().length > 0) {
      employeeRoleList = employeeRoleList.filter((role) =>
        role.address.toLowerCase().includes(this.search.toLowerCase()),
      )
    }

    // Выбранный ПВЗ устанавливается в начало списка
    return employeeRoleList.sort((a, b) => {
      if (a.id === this.selectedPvz?.id) {
        return -1
      }

      if (b.id === this.selectedPvz?.id) {
        return 1
      }

      return 0
    })
  }

  @computed
  get employeeELockRoleList() {
    return this.employeeELockRoleStore.list
  }

  @computed
  get employeeFilteredList() {
    if (this.activeStatus === EmployeeActiveStatus.active) {
      return this.employeeStore.list.filter((item) => !item.isDeleted)
    } else if (this.activeStatus === EmployeeActiveStatus.inactive) {
      return this.employeeStore.list.filter((item) => item.isDeleted)
    }

    return this.employeeStore.list
  }

  @computed
  get employeeActiveList() {
    return this.employeeStore.list.filter((item) => !item.isDeleted)
  }

  @computed
  get employeeInactiveList() {
    return this.employeeStore.list.filter((item) => item.isDeleted)
  }

  @action
  setActiveStatus = (value: EmployeeActiveStatus) => {
    this.activeStatus = value
  }

  @action
  setSelectedStep = (value: number) => {
    this.selectedStep = value
  }

  @action
  setLastName = (value: string) => {
    this.lastName = value
  }

  @action
  setFirstName = (value: string) => {
    this.firstName = value
  }

  @action
  setSnils = (value: string) => {
    this.snils = value
  }

  @action
  setCode = (value: string) => {
    this.code = value
  }

  @action
  setToken = (value: string) => {
    this.token = value
  }

  @action
  setSelectedRole = (value: USER_PERMISSION) => {
    this.selectedRole = value
  }

  @action
  setSelectedELockRole = (canOpeningELock: boolean, canActivateELock: boolean) => {
    if (canOpeningELock && canActivateELock) {
      this.selectedELockRole = ELOCK_PERMISSIONS.openAndActivate
    } else if (canOpeningELock) {
      this.selectedELockRole = ELOCK_PERMISSIONS.open
    } else if (canActivateELock) {
      this.selectedELockRole = ELOCK_PERMISSIONS.activate
    } else {
      this.selectedELockRole = ELOCK_PERMISSIONS.none
    }
  }

  @action
  setSelectedUserId = (value: number) => {
    this.selectedUserId = value
  }

  @action
  setELockRoleList = (value: UserELockRoleRequest[]) => {
    this.eLockRoleList = value
  }

  @action
  setSearch = (value: string) => {
    this.search = value
  }

  reset = () => {
    this.setLastName('')
    this.setFirstName('')
    this.setSnils('')
    this.setCode('')
    this.phoneModel.reset()
    this.setSelectedStep(1)
    this.setSelectedRole(USER_PERMISSION.PERM_EMPLOYEE)
    this.setSelectedELockRole(false, false)
    this.setSelectedUserId(0)
    this.setSearch('')
    this.setActiveStatus(EmployeeActiveStatus.active)
  }

  async getEmployeeList() {
    if (this.selectedPvz?.id) {
      return await this.rootStore.employeeStore.loadList(this.selectedPvz.id, this.selectedPvz.shard)
    }
  }

  async addEmployee() {
    return await this.apiStore.addEmployee()
  }

  async verifyEmployee() {
    return await this.apiStore.verifyEmployee()
  }

  async getUserPvzRoles(selectedUserId: number) {
    return await this.employeeRoleStore.loadList(selectedUserId)
  }

  async getUserELockRoles(selectedUserId: number) {
    return await this.employeeELockRoleStore.loadList(selectedUserId)
  }

  async updateUserRoles() {
    return await this.apiStore.updateUserRoles()
  }

  async updateUserELockRoles() {
    return await this.apiStore.updateUserELockRoles()
  }

  async changeUserSnils(snils: string, userId: number) {
    return await this.apiStore.changeUserSnils(snils, userId)
  }

  async deleteUser(userId: number) {
    return await this.apiStore.deleteUser(userId)
  }

  async recoverUser(userId: number) {
    return await this.apiStore.recoverUser(userId)
  }
}
